type NonUndefined<T extends {}> = {
  [P in keyof T]: undefined extends T[P] ? never : T[P]
}

function nonNullable<T extends any>(value: T): value is NonNullable<T> {
  return value !== null && typeof value !== 'undefined'
}

function nonEmptyFields<T extends {}>(obj: T): NonUndefined<T> {
  const result: any = {}
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val !== undefined && val !== '') {
      result[key] = val
    }
  }
  return result
}

type Pairs<T> = {
  [K in keyof T]: [K, T[K]]
}[keyof T][]

export function pairs<T extends {}>(obj: T): Pairs<Required<T>> {
  return Object.entries(obj) as Pairs<Required<T>>
}

export const helpersObject = {
  nonNullable,
  nonEmptyFields,
  pairs,
}

export const isPlainObject = (obj: unknown) => {
  return obj !== null && Object.prototype.toString.call(obj) === '[object Object]'
}

/**
 * Creates key:value representation of given nested object. Where "key" is lodash like object path.
 * Example input:
 * const obj = {
 *   a: { b: { c: 1 } },
 *   v: [[ 1 ]],
 *   d: [{ x: 6, s: [1, { z: { c: { b: 'hello' } } }] }]
 * }
 * Example output:
 * {
 *   'a.b.c': 1,
 *   'v[0][0]': 1,
 *   'd[0].x': 6,
 *   'd[0].s[0]': 1,
 *   'd[0].s[1].z.c.b': 'hello'
 * }
 */
export const flat = (obj: unknown): Record<string, unknown> => {
  if (!isPlainObject(obj) && !Array.isArray(obj)) {
    return {}
  }
  const result = {}

  const walk = (obj, path = '') => {
    if (isPlainObject(obj)) {
      return Object.keys(obj).forEach(key => {
        walk(obj[key], path.length ? `${path}.${key}` : key)
      })
    }
    if (Array.isArray(obj)) {
      return obj.forEach((obj, i) => {
        walk(obj, `${path}[${i}]`)
      })
    }

    result[path] = obj
  }
  walk(obj, '')
  return result
}

/**
 * Like flat, but reverted. See {@link flat}
 * Example input:
 * const obj = {
 *   'a.b.c': 1,
 *   'v[0][0]': 1,
 *   'd[0].x': 6,
 *   'd[0].s[0]': 1,
 *   'd[0].s[1].z.c.b': 'hello'
 * }
 * Example output:
 * {
 *   a: { b: { c: 1 } },
 *   v: [[ 1 ]],
 *   d: [{ x: 6, s: [1, { z: { c: { b: 'hello' } } }] }]
 * }
 */
type UnflatResult = { [index: string]: unknown }
export const unflat = (obj: Record<string, unknown>): UnflatResult | UnflatResult[] => {
  type Token =
    | {
        type: 'key'
        key: string
      }
    | {
        type: 'arrayKey'
        key: number
      }
  // Captives order:
  // [1] dot (skipping)
  // [2] regular key
  // [3] array key
  // [4] unexpected token
  const rxToken = /(\.)|([\w\-_]+)|(\[\d+\])|([^\w\[\]]|\[\D+\]?)/y
  const rxIndex = /\[(\d)\]/

  const tokenize = (key: string): Token[] => {
    rxToken.lastIndex = 0
    const result: Token[] = []

    let captives
    while ((captives = rxToken.exec(key))) {
      if (captives[4]) {
        console.warn(`Failed to parse key "${key}": unexpected token "${captives[4]}" at ${rxToken.lastIndex}`)
        return []
      }
      if (captives[3]) {
        const [, index] = rxIndex.exec(captives[3]) as RegExpExecArray
        result.push({
          type: 'arrayKey',
          key: parseInt(index, 10),
        })
      }
      if (captives[2]) {
        result.push({
          type: 'key',
          key: captives[2],
        })
      }
    }
    return result
  }

  let root
  for (const [key, value] of Object.entries(obj)) {
    const tokens = tokenize(key)
    if (!root && tokens[0]) {
      switch (tokens[0].type) {
        case 'key':
          root = {}
          break
        case 'arrayKey':
          root = []
          break
      }
    }
    let pointer = root
    const lastIndex = tokens.length - 1
    tokens.forEach((token, index) => {
      if (index === lastIndex) {
        pointer[token.key] = value
      } else if (!pointer[token.key]) {
        switch (tokens[index + 1].type) {
          case 'key':
            pointer[token.key] = {}
            break
          case 'arrayKey':
            pointer[token.key] = []
            break
        }
      }
      pointer = pointer[token.key]
    })
  }

  return root
}
